import { DashboardLoadingScreen } from "@/components/dashboard-loading-screen"
import { sessionValues } from "@/storage/browser"
import { getDeployedBuildID } from "@/transport/app-build"
import { HttpError } from "@/transport/http"
import { reloadWithCacheBust } from "@/utils/reload"
import { createContext, createSignal, onCleanup, type ParentProps, useContext } from "solid-js"

const chunkReloadKey = "mux.chunk-reload-attempted"
const chunkReloadCooldownMs = 60_000
const reachabilityTimeoutMs = 5_000
const offlineRetryMinMs = 2_000
const offlineRetryMaxMs = 30_000

export function isChunkLoadError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error ?? "")
  return /failed to fetch dynamically imported module|error loading dynamically imported module|dynamic import failed|importing a module script failed/i.test(message)
}

/** "reloading" checks the connection, then reloads; "offline" waits for it to return. */
export type ChunkRecovery = "idle" | "reloading" | "offline"

type RejectionTarget = {
  addEventListener: (type: "unhandledrejection", listener: (event: PromiseRejectionEvent) => void) => void
  removeEventListener: (type: "unhandledrejection", listener: (event: PromiseRejectionEvent) => void) => void
}

type ConnectivityTarget = {
  addEventListener: (type: "online", listener: () => void) => void
  removeEventListener: (type: "online", listener: () => void) => void
}

type ChunkReloadOptions = {
  target?: RejectionTarget
  connectivity?: ConnectivityTarget
  storage?: Pick<Storage, "getItem" | "setItem">
  now?: () => number
  jitter?: () => number
  reload?: () => void
  /** Resolves when this origin answers. */
  probe?: (signal: AbortSignal) => Promise<unknown>
  online?: () => boolean
  setTimer?: (callback: () => void, delay: number) => number
  clearTimer?: (timer: number) => void
}

export function createChunkReloadController(options: ChunkReloadOptions = {}) {
  const target: RejectionTarget | undefined = options.target ?? (typeof window === "undefined" ? undefined : {
    addEventListener: (type, listener) => window.addEventListener(type, listener),
    removeEventListener: (type, listener) => window.removeEventListener(type, listener),
  })
  const connectivity: ConnectivityTarget | undefined = options.connectivity ?? (typeof window === "undefined" ? undefined : {
    addEventListener: (type, listener) => window.addEventListener(type, listener),
    removeEventListener: (type, listener) => window.removeEventListener(type, listener),
  })
  const storage = options.storage ?? sessionValues
  const now = options.now ?? Date.now
  const jitter = options.jitter ?? (() => 150)
  const reload = options.reload ?? reloadWithCacheBust
  const probe = options.probe ?? getDeployedBuildID
  const online = options.online ?? (() => typeof navigator === "undefined" || navigator.onLine !== false)
  const setTimer = options.setTimer ?? ((callback, delay) => window.setTimeout(callback, delay))
  const clearTimer = options.clearTimer ?? ((handle) => window.clearTimeout(handle))
  const [recovery, setRecovery] = createSignal<ChunkRecovery>("idle")
  let timer: number | undefined
  let checking: AbortController | undefined
  let offlineRetries = 0
  let disposed = false

  const cancelTimer = () => {
    if (timer === undefined) return
    clearTimer(timer)
    timer = undefined
  }

  // Any HTTP answer, even an error status, shows the origin can serve a reload.
  const reachable = async () => {
    if (!online()) return false
    const controller = new AbortController()
    checking = controller
    const timeout = setTimer(() => controller.abort(), reachabilityTimeoutMs)
    try {
      await probe(controller.signal)
      return true
    } catch (error) {
      return error instanceof HttpError
    } finally {
      clearTimer(timeout)
      if (checking === controller) checking = undefined
    }
  }

  // A missing chunk usually means a deploy replaced it, and a reload fixes
  // that. Offline the reload cannot load either and leaves a blank page, so
  // wait until the origin answers.
  const recover = async () => {
    cancelTimer()
    const ok = await reachable()
    if (disposed) return
    if (!ok) {
      setRecovery("offline")
      const delay = Math.min(offlineRetryMaxMs, offlineRetryMinMs * 2 ** offlineRetries++)
      timer = setTimer(() => {
        timer = undefined
        void recover()
      }, delay)
      return
    }
    setRecovery("reloading")
    timer = setTimer(() => {
      timer = undefined
      try {
        storage.setItem(chunkReloadKey, String(now()))
      } catch {}
      reload()
    }, jitter())
  }

  const schedule = (error: unknown) => {
    if (!isChunkLoadError(error)) return false
    if (recovery() !== "idle") return true

    let lastAttempt = 0
    try {
      lastAttempt = Number(storage.getItem(chunkReloadKey) || "0")
    } catch {
      // Storage can be blocked; reloading is still preferable to a bricked app.
    }
    if (lastAttempt > 0 && now() - lastAttempt < chunkReloadCooldownMs) return false

    setRecovery("reloading")
    void recover()
    return true
  }

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (!schedule(event.reason)) return
    event.preventDefault()
  }
  const onOnline = () => {
    if (recovery() !== "offline" || checking) return
    void recover()
  }
  target?.addEventListener("unhandledrejection", onUnhandledRejection)
  connectivity?.addEventListener("online", onOnline)

  return {
    schedule,
    recovery,
    dispose() {
      disposed = true
      target?.removeEventListener("unhandledrejection", onUnhandledRejection)
      connectivity?.removeEventListener("online", onOnline)
      checking?.abort()
      cancelTimer()
    },
  }
}

type ChunkReload = Pick<ReturnType<typeof createChunkReloadController>, "schedule" | "recovery">
const ChunkReloadContext = createContext<ChunkReload>()

export function ChunkReloadController(props: ParentProps) {
  const controller = createChunkReloadController()
  onCleanup(controller.dispose)
  return <ChunkReloadContext.Provider value={controller}>{props.children}</ChunkReloadContext.Provider>
}

function useChunkReloadContext() {
  const context = useContext(ChunkReloadContext)
  if (!context) throw new Error("useChunkReload must be used inside ChunkReloadController")
  return context
}

export function useChunkReload() {
  return useChunkReloadContext().schedule
}

/** Shown in place of a page whose code failed to load while recovery runs. */
export function ChunkRecoveryScreen() {
  const { recovery } = useChunkReloadContext()
  return (
    <DashboardLoadingScreen
      label={recovery() === "offline" ? "You're offline. Kettle will reload when your connection is back." : "Reloading Kettle…"}
    />
  )
}
